import { Amplify } from 'aws-amplify';

import type { CognitoConfig } from '../types';

// Default configuration for different environments
export const DEFAULT_COGNITO_CONFIG: Record<string, CognitoConfig> = {
  development: {
    region: process.env.REACT_APP_AWS_REGION || 'eu-west-1',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID || '',
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
  },
  staging: {
    region: process.env.REACT_APP_AWS_REGION || 'eu-west-1',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID || '',
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
  },
  production: {
    region: process.env.REACT_APP_AWS_REGION || 'eu-west-1',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID || '',
    identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
  },
};

/**
 * Configure AWS Amplify with Cognito settings
 */
export function configureAuth(config: CognitoConfig): void {
  const cognitoConfig: any = {
    region: config.region,
    userPoolId: config.userPoolId,
    userPoolClientId: config.userPoolWebClientId,
  };

  // Only add identityPoolId if it exists
  if (config.identityPoolId) {
    cognitoConfig.identityPoolId = config.identityPoolId;
  }

  // Add OAuth configuration if provided
  if (config.oauth) {
    cognitoConfig.loginWith = {
      oauth: {
        domain: config.oauth.domain,
        scopes: config.oauth.scope,
        redirectSignIn: [config.oauth.redirectSignIn],
        redirectSignOut: [config.oauth.redirectSignOut],
        responseType: config.oauth.responseType as 'code' | 'token',
      },
    };
  }

  const amplifyConfig = {
    Auth: {
      Cognito: cognitoConfig,
    },
  };

  Amplify.configure(amplifyConfig);
}

/**
 * Get configuration for current environment
 */
export function getEnvironmentConfig(): CognitoConfig {
  const environment = process.env.NODE_ENV || 'development';
  return (
    DEFAULT_COGNITO_CONFIG[environment] || DEFAULT_COGNITO_CONFIG.development
  );
}

/**
 * Initialize authentication with environment-specific configuration
 */
export function initializeAuth(customConfig?: Partial<CognitoConfig>): void {
  const envConfig = getEnvironmentConfig();
  const finalConfig = { ...envConfig, ...customConfig };

  // Validate required configuration
  if (!finalConfig.userPoolId || !finalConfig.userPoolWebClientId) {
    throw new Error(
      'Missing required Cognito configuration: userPoolId and userPoolWebClientId are required'
    );
  }

  configureAuth(finalConfig);
}
